import { useMemo } from 'react';
import { renderMetaTags, SeoLinkTag } from 'react-datocms/seo';
import DatadogInit from 'components/DatadogInit';
import GoogleTagManager from 'components/GoogleTagManager';
import Redirect from 'components/Redirect';
import { isProd } from 'constants/environment';
import useEnvUrl from 'hooks/useEnvUrl';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { PostHogProvider } from 'posthog-js/react';
import type { fetchBlogCategories, fetchBlogPost } from 'services/blog/ssr';
import ConfigProvider from 'services/config/ConfigProvider';
import { fetchBrandConfig } from 'services/config/ssr';
import { Locale } from 'services/graphql/types';
import type { fetchPage } from 'services/page/ssr';
import { notEmpty } from 'utils/arrays';
import { StaticProps } from 'utils/mergeStaticProps';

import '@carbonify/ui/global.css';
import '@carbonify/ui/fonts.css';

interface LandingsAppProps extends AppProps {
  pageProps: StaticProps<typeof fetchBrandConfig> & StaticProps<typeof fetchBlogCategories>;
}

const tenantsWithMonitoring = ['carbonify', 'carbonifyat', 'smartcarb'];

export default function App({ Component, pageProps }: LandingsAppProps) {
  const {
    tenant,
    alternateLinks: linkedLocales,
    locale,
    config: { theme, browser, ...config } = {} as (typeof pageProps)['config'],
    ...rest
  } = pageProps;

  const pageConfig = (rest as unknown as StaticProps<typeof fetchPage>)?.page;
  const postBlogConfig = (rest as unknown as StaticProps<typeof fetchBlogPost>)?.blogPost;
  const getUrl = useEnvUrl();
  const jsonLdSchemaOrg = useMemo(() => {
    const values = postBlogConfig?.jsonLdSchemaOrg || pageConfig?.jsonLdSchemaOrg;
    if (!values || !Object.keys(values).length) return null;

    return JSON.stringify(values, null, ' ');
  }, [postBlogConfig, pageConfig]);

  const alternateLinks = useMemo(() => {
    const pageSlugLocales = (postBlogConfig?._allSlugLocales || pageConfig?._allSlugLocales || []).reduce(
      (result, { locale, value }) => {
        result[locale] = value;
        return result;
      },
      {} as Record<Locale, string>,
    );

    const prefix = postBlogConfig ? 'blog/' : '/';

    return linkedLocales
      ?.map(({ value, locale }) => {
        const slug = pageSlugLocales[locale];

        return {
          locale,
          label: locale.split('_')[1] || locale.toUpperCase(),
          href: getUrl(prefix + (slug || ''), value),
        };
      })
      ?.filter(notEmpty);
  }, [linkedLocales, postBlogConfig, pageConfig, getUrl]);

  const metaTags = useMemo(() => {
    if (!config.settings) return null;

    const { _seoMetaTags: seoTags, favicon } = config.settings;
    const pageTags = postBlogConfig?._seoMetaTags || pageConfig?._seoMetaTags || [];
    const faviconTags: SeoLinkTag[] = favicon?.url
      ? [
          {
            tag: 'link',
            attributes: {
              sizes: '32x32',
              type: 'image/png',
              rel: 'icon',
              href: `${favicon.url}?w=32&h=32&fm=png`,
            },
          },
          {
            tag: 'link',
            attributes: {
              sizes: '180x180',
              type: 'image/png',
              rel: 'apple-touch-icon',
              href: `${favicon.url}?w=180&h=180&fm=png`,
            },
          },
        ]
      : [];

    const pageTitle = pageTags.find(({ tag }) => tag === 'title')?.content;
    const rootTitle = seoTags.find(({ tag }) => tag === 'title')?.content || config.brandName;
    const titleTags = pageTitle ? [{ tag: 'title' as const, content: [pageTitle, '•', rootTitle].join(' ') }] : [];
    const defaultLocale = alternateLinks.find(({ locale }) => locale === 'de_DE');
    const canonicalTag = [{ tag: 'link' as const, attributes: { href: defaultLocale?.href, rel: 'canonical' } as any }];
    const alternateTags = alternateLinks
      .concat(defaultLocale ? [{ ...defaultLocale, locale: 'x-default' as Locale }] : [])
      .map(({ href, locale }) => ({
        tag: 'link' as const,
        attributes: { href, hrefLang: locale.replace('_', '-'), rel: 'alternate' } as any,
      }));

    const hasUtmsConditions = pageConfig?.showOnlyForUtms && Object.keys(pageConfig.showOnlyForUtms).length > 0;

    const indexingTags = hasUtmsConditions
      ? [
          {
            tag: 'meta' as const,
            content: null,
            attributes: {
              name: 'robots',
              content: 'noindex',
            },
          },
        ]
      : [];

    return renderMetaTags([
      ...seoTags,
      ...pageTags,
      ...indexingTags,
      ...faviconTags,
      ...titleTags,
      ...canonicalTag,
      ...alternateTags,
    ]).map((items, index) => ({ ...items, key: items.key === 'link-alternate' ? `link-${index}` : items.key }));
  }, [config.brandName, config.settings, pageConfig, postBlogConfig, alternateLinks]);

  const { redirectType = 'none', redirectUrl } = pageConfig || postBlogConfig || {};

  return (
    <ConfigProvider
      config={config}
      alternateLinks={alternateLinks}
      categories={pageProps.categories}
    >
      <PostHogProvider
        apiKey={process.env.NEXT_PUBLIC_POSTHOG_API_KEY || ''}
        options={{
          api_host: 'https://hugs.carbonify.de',
          ui_host: 'https://eu.posthog.com',
          autocapture: false,
          persistence: 'localStorage+cookie',
          person_profiles: 'identified_only',
          capture_performance: isProd ? tenantsWithMonitoring.includes(tenant) : false,
          capture_pageview: false,
          capture_pageleave: false,
          capture_heatmaps: isProd ? tenantsWithMonitoring.includes(tenant) : false,
          capture_exceptions: false,
          capture_dead_clicks: false,
          opt_in_site_apps: true,
          loaded: (posthog) => {
            posthog.group('tenant', tenant);
            posthog.onFeatureFlags(() => {
              if (posthog.isFeatureEnabled('enable-autocapture')) {
                posthog.set_config({ autocapture: true });
              }
              if (posthog.isFeatureEnabled('enable-capturing')) {
                const { capture_exceptions, capture_pageleave, capture_pageview, capture_pageview_history } =
                  posthog.getFeatureFlagPayload('enable-capturing') as never;

                posthog.set_config({
                  capture_pageview: capture_pageview_history ? 'history_change' : (capture_pageview ?? false),
                  capture_pageleave: capture_pageleave ?? false,
                  capture_exceptions: capture_exceptions ?? false,
                });
                if (posthog && capture_pageview && typeof window !== 'undefined') {
                  posthog.capture('$pageview', {
                    title: document.title,
                    path: location.pathname + location.search,
                  });
                }
              }
              if (posthog.isFeatureEnabled('enable-heatmaps')) {
                posthog.set_config({
                  capture_heatmaps: true,
                  capture_dead_clicks: true,
                });
              }
            });
          },
        }}
      >
        <Head>
          {theme && <style type="text/css">{theme}</style>}
          <meta
            name="theme-color"
            content={browser?.color}
          />
          <meta
            name="viewport"
            content="initial-scale=1, width=device-width, user-scalable=yes, viewport-fit=cover"
          />
          {metaTags}
          {jsonLdSchemaOrg && <script type="application/ld+json">{jsonLdSchemaOrg}</script>}
        </Head>
        <DatadogInit tenant={tenant} />
        <GoogleTagManager tenant={tenant} />
        {redirectType === 'none' || !redirectUrl ? (
          <Component {...rest} />
        ) : (
          <Redirect
            url={redirectUrl}
            permanent={redirectType === '301'}
          />
        )}
      </PostHogProvider>
    </ConfigProvider>
  );
}
